import React from 'react';
import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '../components/ui/card';
import { ArrowRight, Briefcase, TrendingUp, Shield, MapPin, Building, Users, UserCheck, Handshake } from 'lucide-react';
import Header from '../components/Header';
import Footer from '../components/Footer';
import HeroCarousel from '../components/HeroCarousel';
import { productCategories, stats } from '../mock';

const Home = () => {
  const iconMap = {
    'briefcase': Briefcase,
    'trending-up': TrendingUp,
    'shield': Shield
  };

  const statsIconMap = {
    'map-pin': MapPin,
    'building': Building,
    'users': Users,
    'user-check': UserCheck,
    'handshake': Handshake
  };

  return (
    <div className="min-h-screen">
      <Header />
      
      {/* Hero Carousel */}
      <div className="pt-20">
        <HeroCarousel />
      </div>

      {/* Stats Section */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-5 gap-6 max-w-7xl mx-auto">
            {stats.map((stat) => {
              const IconComponent = statsIconMap[stat.icon] || Building;
              return (
                <div 
                  key={stat.id} 
                  className="bg-gradient-to-br from-white to-gray-50 rounded-xl p-6 shadow-lg hover:shadow-xl transition-all duration-300 border-2 border-gray-100 hover:border-[#DC143C] group"
                >
                  <div className="flex items-center justify-between mb-3">
                    <div className="w-12 h-12 bg-red-50 rounded-lg flex items-center justify-center group-hover:bg-[#DC143C] transition-colors duration-300">
                      <IconComponent className="w-6 h-6 text-[#DC143C] group-hover:text-white transition-colors duration-300" />
                    </div>
                  </div>
                  <div className="text-4xl font-bold text-[#2C3E50] mb-1">{stat.value}</div>
                  <div className="text-sm text-gray-600 font-medium">{stat.label}</div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Products Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-white">
        <div className="container mx-auto">
          <div className="text-center max-w-3xl mx-auto mb-16">
            <h2 className="text-4xl sm:text-5xl font-bold text-gray-900 mb-4">
              Our Services
            </h2>
            <p className="text-xl text-gray-600">
              Comprehensive financial solutions tailored to your needs
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {productCategories.map((category) => {
              const IconComponent = iconMap[category.icon];
              return (
                <Link key={category.id} to={category.path}>
                  <Card className="group hover:shadow-2xl transition-all duration-300 border-2 border-gray-200 hover:border-[#DC143C] cursor-pointer h-full">
                    <CardHeader className="text-center pb-6">
                      <div className="w-20 h-20 bg-gradient-to-br from-[#DC143C] to-[#B8112A] rounded-2xl flex items-center justify-center mb-6 mx-auto group-hover:scale-110 transition-transform duration-300">
                        <IconComponent className="w-10 h-10 text-white" />
                      </div>
                      <CardTitle className="text-2xl font-bold text-gray-900 mb-3 group-hover:text-[#DC143C] transition-colors">
                        {category.title}
                      </CardTitle>
                      <CardDescription className="text-base text-gray-600 leading-relaxed">
                        {category.description}
                      </CardDescription>
                    </CardHeader>
                    <CardContent className="text-center pb-8">
                      <Button className="bg-[#2C3E50] hover:bg-[#DC143C] text-white group-hover:bg-[#DC143C] transition-colors duration-300">
                        Explore Services
                        <ArrowRight className="ml-2 w-4 h-4" />
                      </Button>
                    </CardContent>
                  </Card>
                </Link>
              );
            })}
          </div>
        </div>
      </section>

      {/* CTA Section */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-gradient-to-r from-[#2C3E50] to-[#3d5568]">
        <div className="container mx-auto text-center">
          <h2 className="text-4xl font-bold text-white mb-6">
            Ready to Take Control of Your Financial Future?
          </h2>
          <p className="text-xl text-gray-200 mb-8 max-w-2xl mx-auto">
            Connect with our expert advisors and discover the perfect financial solution for you
          </p>
          <Link to="/contact">
            <Button className="bg-[#DC143C] hover:bg-[#B8112A] text-white px-10 py-6 text-lg font-semibold shadow-lg hover:shadow-xl transition-all duration-300">
              Contact Us Today
              <ArrowRight className="ml-2 w-5 h-5" />
            </Button>
          </Link>
        </div>
      </section>

      <Footer />
    </div>
  );
};

export default Home;